import { Security } from '../proto/generated/candle-data-depot-common'
import { Condition } from '../proto/generated/nexus'
import { Order, OrderExecutionParams, OrderStatus, OrderType } from '../proto/generated/order'
import {
    ConditionalEntryState,
    hasConditionalRuleContent,
    orderExecutionParamsForConditionalEntry,
} from './conditionalEntry'
import { ORDER_TYPES_STRATEGY_AUXILIARY } from './orders'

export type QuickTradePlaceType = 'trade' | 'modify' | 'notTradable'

export type QuickTradeConditionalPlacePlan = {
    entryParams: OrderExecutionParams | undefined
    mainOrderType: OrderType
    mainExecutionParams: OrderExecutionParams | undefined
    omitMainOrder: boolean
    appendConditionalExitLeg: boolean
    appendConditionalEntryLeg: boolean
}

export function pickMergedConditionForOrderModify(input: {
    orderCondition?: Condition
    modifyingOrder?: Pick<Order, 'orderType' | 'orderExecutionParams'>
    exitSibling?: Pick<Order, 'orderExecutionParams'> | undefined
    entrySibling?: Pick<Order, 'orderExecutionParams'> | undefined
}): Condition | undefined {
    const m = input.modifyingOrder
    const ot = m?.orderType
    if (ot === OrderType.CONDITIONAL_ENTRY || ot === OrderType.CONDITIONAL_EXIT) {
        return m?.orderExecutionParams?.condition ?? input.orderCondition
    }
    return (
        input.orderCondition ??
        input.entrySibling?.orderExecutionParams?.condition ??
        input.exitSibling?.orderExecutionParams?.condition ??
        m?.orderExecutionParams?.condition
    )
}

/** CE merge for modify; ignores exit sibling (CX stays on its leg). */
export function pickMergedConditionForOrderModifyIgnoringExitSibling(input: {
    orderCondition?: Condition
    modifyingOrder?: Pick<Order, 'orderType' | 'orderExecutionParams'>
    entrySibling?: Pick<Order, 'orderExecutionParams'> | undefined
}): Condition | undefined {
    const m = input.modifyingOrder
    const ot = m?.orderType
    if (ot === OrderType.CONDITIONAL_ENTRY || ot === OrderType.CONDITIONAL_EXIT) {
        return m?.orderExecutionParams?.condition ?? input.orderCondition
    }
    return (
        input.orderCondition ??
        input.entrySibling?.orderExecutionParams?.condition ??
        m?.orderExecutionParams?.condition
    )
}

export function buildQuickTradeConditionalPlacePlan(input: {
    placeType: QuickTradePlaceType
    isSquareOffOrder: boolean
    mode: 'regular' | 'trigger' | 'iceberg' | 'slOrTarget'
    orderType: OrderType
    modifyingOrderType: OrderType | undefined
    conditionalState: ConditionalEntryState
    baseExecutionParams: OrderExecutionParams | undefined
    useSeparateConditionalEntryLeg?: boolean
}): QuickTradeConditionalPlacePlan {
    const useSep = input.useSeparateConditionalEntryLeg === true
    const entryParams = orderExecutionParamsForConditionalEntry(
        input.baseExecutionParams,
        input.conditionalState,
        { stripWhenDisabled: input.placeType !== 'modify' }
    )
    const hasCondition = hasConditionalRuleContent(entryParams?.condition)
    const modExitLeg =
        input.placeType === 'modify' && input.modifyingOrderType === OrderType.CONDITIONAL_EXIT
    const modEntryLeg =
        input.placeType === 'modify' && input.modifyingOrderType === OrderType.CONDITIONAL_ENTRY

    // Exit leg is only for square-off (separate CX order). Modifying a NORMAL (regular) order to add an
    // entry rule upgrades that same order to CONDITIONAL_ENTRY with `orderExecutionParams` — never append a sibling here.
    const appendConditionalExitLeg =
        hasCondition && !modExitLeg && !useSep && input.isSquareOffOrder

    let appendConditionalEntryLeg =
        useSep &&
        hasCondition &&
        !modExitLeg &&
        !modEntryLeg &&
        (input.placeType !== 'modify' ||
            (input.modifyingOrderType !== OrderType.CONDITIONAL_ENTRY &&
                input.modifyingOrderType !== OrderType.CONDITIONAL_EXIT))

    let mainOrderType = input.orderType
    let mainExecutionParams: OrderExecutionParams | undefined

    if (hasCondition && useSep) {
        if (modEntryLeg) {
            mainOrderType = OrderType.CONDITIONAL_ENTRY
            mainExecutionParams = entryParams
            appendConditionalEntryLeg = false
        } else if (!modExitLeg) {
            mainOrderType = input.orderType
            const base = input.baseExecutionParams
            if (base?.condition !== undefined) {
                mainExecutionParams = { ...base, condition: undefined }
            }
        }
    } else if (hasCondition) {
        if (modExitLeg) {
            mainOrderType = OrderType.CONDITIONAL_EXIT
            mainExecutionParams = entryParams
        } else if (appendConditionalExitLeg) {
            mainOrderType =
                input.orderType === OrderType.TRIGGER || input.mode === 'trigger'
                    ? OrderType.TRIGGER
                    : OrderType.NORMAL
        } else if (
            input.placeType === 'modify' &&
            input.modifyingOrderType === OrderType.CONDITIONAL_ENTRY
        ) {
            mainOrderType = OrderType.CONDITIONAL_ENTRY
            mainExecutionParams = entryParams
        } else {
            mainOrderType = OrderType.CONDITIONAL_ENTRY
            mainExecutionParams = entryParams
        }
    } else if (
        input.orderType === OrderType.CONDITIONAL_ENTRY ||
        input.orderType === OrderType.CONDITIONAL_EXIT
    ) {
        mainOrderType = OrderType.NORMAL
    }

    return {
        entryParams,
        mainOrderType,
        mainExecutionParams,
        omitMainOrder: input.isSquareOffOrder && appendConditionalExitLeg,
        appendConditionalExitLeg,
        appendConditionalEntryLeg,
    }
}

export function findStandaloneSquareOffEntryBatch(
 orderHistory: Order[] | undefined,
    groupId: string,
    security: Security
): string | undefined {
    return (orderHistory ?? []).find(
        (o) =>
            o.basketId === groupId &&
            o.security?.code === security.code &&
            o.security?.exchange === security.exchange &&
            !ORDER_TYPES_STRATEGY_AUXILIARY.includes(o.orderType) &&
            (o.status === OrderStatus.EXECUTED || o.status === OrderStatus.PARTIALLY_FILLED)
    )?.batchId
}

export function findStandaloneConditionalExitLeg(
    orderHistory: Order[] | undefined,
    groupId: string,
    security: Security,
    entryBatchId: string | undefined
): Order | undefined {
    const pool = (orderHistory ?? []).filter((o) => {
        if (o.basketId !== groupId) return false
        if (o.orderType !== OrderType.CONDITIONAL_EXIT) return false
        if (o.security?.code !== security.code || o.security?.exchange !== security.exchange) return false
        if (entryBatchId != null && o.batchId !== entryBatchId) return false
        return true
    })
    return (
        pool.find((o) => o.status === OrderStatus.INACTIVE) ??
        pool.find((o) =>
            [OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(
                o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN
            )
        )
    )
}

